/**
 * Katie & Laura — Passport product foil mid-stack inject (staging)
 * -----------------------------------------------------------------
 * Reads product.handle from <product-gallery data-handle>,
 * fetches passport-foil-map-v1.json (data-foil-map-url),
 * inserts embossed foil slide AFTER the 3rd .product-gallery__media
 * (never top 3). Short stacks: append after last; never is-initial.
 * Mirrors thumb + page-dot so Prestige carousel stays in sync.
 * Save button uses [data-kl-passport-save] → kl-passport-save.js.
 * Theme-only overlay — does NOT Admin-mutate product.media.
 * Soft no-op if handle missing from map or embossed asset 404.
 *
 * HOTFIX 2026-09-15 (Katie): foil must expand to full resolution in Prestige
 * lightbox/zoom. Prestige PhotoSwipe gathers `:scope > img` on each
 * .product-gallery__media — embossed <img> MUST be a direct child (not nested
 * in a frame), with width/height attrs for zoom math.
 */
(function () {
  'use strict';

  var STYLE_ID = 'kl-product-foil-inject-css';
  var SLOT = 3; // 0-based: after 3rd origin image → 4th visual slot

  function ensureCss() {
    if (document.getElementById(STYLE_ID)) return;
    var css = document.createElement('style');
    css.id = STYLE_ID;
    css.textContent = [
      '.kl-product-foil-slide{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;}',
      /* Direct-child img required for Prestige PhotoSwipe `:scope > img` lightbox path */
      '.kl-product-foil-slide > img.kl-product-foil-slide__img,.kl-product-foil-slide__img{display:block;width:100%;height:auto;max-height:min(80vh,720px);object-fit:contain;border-radius:8px;cursor:zoom-in;}',
      '.kl-product-foil-slide__frame{width:100%;display:flex;align-items:center;justify-content:center;}',
      '.kl-passport-save-btn{appearance:none;margin:12px auto 4px;display:inline-flex;align-items:center;gap:8px;',
      'padding:10px 16px;border-radius:999px;border:1px solid rgba(201,164,106,0.65);',
      'background:#fff8fb;color:#5a3a48;font:600 13px/1.2 "Segoe UI","Avenir Next",sans-serif;cursor:pointer;',
      'box-shadow:0 2px 8px rgba(196,93,134,0.12);position:relative;z-index:2;}',
      '.kl-passport-save-btn:hover,.kl-passport-save-btn:focus-visible{border-color:#c45d86;outline:2px solid rgba(196,93,134,0.35);outline-offset:2px;}',
      '.kl-passport-save-btn__icon,.kl-passport-save-btn [aria-hidden]{color:#c9a46a;}',
      '.product-gallery__thumbnail[data-kl-foil-thumb] img{object-fit:contain;}',
      '.kl-passport-toast{position:fixed;z-index:10000;left:50%;bottom:24px;transform:translateX(-50%);',
      'max-width:min(92vw,360px);padding:12px 16px;border-radius:12px;background:#5a3a48;color:#fff8fb;',
      'font:600 13px/1.35 "Segoe UI",sans-serif;box-shadow:0 8px 24px rgba(0,0,0,0.25);}',
      '.kl-passport-toast[hidden]{display:none!important;}'
    ].join('');
    document.head.appendChild(css);
  }

  function assetBaseFromMapUrl(mapUrl) {
    return String(mapUrl || '').replace(/[^/]+$/, '');
  }

  /** Insert node after the Nth item (1-based N=3 → after index 2). Short: after last. */
  function afterThird(list, node) {
    if (!list || !list.length || !node) return false;
    if (list.length >= SLOT) {
      list[SLOT - 1].after(node);
    } else {
      list[list.length - 1].after(node);
    }
    return true;
  }

  function buildSlide(row, assetBase) {
    var wrap = document.createElement('div');
    wrap.className = 'product-gallery__media snap-center kl-product-foil-slide';
    wrap.setAttribute('data-media-type', 'image');
    wrap.setAttribute('data-kl-foil-slide', '');
    wrap.setAttribute('data-kl-foil-slot', String(SLOT));
    wrap.setAttribute('data-handle', row.handle);
    wrap.setAttribute('data-title', row.title || '');
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Passport embossed foil');
    // never is-initial — keep Prestige focus on origin photos

    /*
     * HOTFIX 2026-09-15: Prestige openLightBox does:
     *   cells.flatMap(cell => cell.querySelectorAll(':scope > img'))
     * Nested frame wrappers made foil invisible to lightbox/zoom.
     * Embossed <img> MUST be a direct child of .product-gallery__media.
     */
    var img = document.createElement('img');
    img.className = 'kl-product-foil-slide__img';
    img.alt = row.title || row.handle;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = assetBase + row.embossed;
    /* PhotoSwipe zoom math needs width/height; refine on load */
    img.setAttribute('width', '1600');
    img.setAttribute('height', '1600');
    img.addEventListener('load', function () {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        img.setAttribute('width', String(img.naturalWidth));
        img.setAttribute('height', String(img.naturalHeight));
      }
    });
    wrap.appendChild(img);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kl-passport-save-btn kl-passport-save-btn--foil';
    btn.setAttribute('data-kl-passport-save', '');
    btn.setAttribute('data-handle', row.handle);
    btn.setAttribute('data-title', row.title || '');
    btn.setAttribute('aria-label', 'Save to Passport');
    btn.innerHTML =
      '<span class="kl-passport-save-btn__icon" aria-hidden="true">✦</span>' +
      '<span class="kl-passport-save-btn__label">Save to Passport</span>';
    wrap.appendChild(btn);
    return { wrap: wrap, img: img };
  }

  function buildThumb(row, assetBase) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'product-gallery__thumbnail';
    btn.setAttribute('data-kl-foil-thumb', '');
    btn.setAttribute('data-kl-foil-slot', String(SLOT));
    btn.setAttribute('aria-current', 'false');
    btn.setAttribute('aria-label', 'Passport foil — ' + (row.title || row.handle));
    var img = document.createElement('img');
    img.className = 'object-contain product-gallery__thumbnail-image';
    img.alt = '';
    img.loading = 'lazy';
    // HOTFIX 2026-09-14: MAIN has embossed jpgs only (no pf-*-256.png). Prefer embossed
    // for thumbs so gallery never shows a broken icon; optional stamp thumb if present later.
    var thumbName = row.embossed || row.thumb || row.thumb_256;
    img.src = assetBase + thumbName;
    img.width = 56;
    img.height = 56;
    // If a stamp thumb exists on CDN, swap after probe (non-blocking).
    if (row.thumb || row.thumb_256) {
      var stamp = assetBase + (row.thumb || row.thumb_256);
      var probe = new Image();
      probe.onload = function () { img.src = stamp; };
      probe.src = stamp;
    }
    btn.appendChild(img);
    return btn;
  }

  function buildDot() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tap-area';
    btn.setAttribute('data-kl-foil-dot', '');
    btn.setAttribute('aria-current', 'false');
    var sr = document.createElement('span');
    sr.className = 'sr-only';
    sr.textContent = 'Passport foil';
    btn.appendChild(sr);
    return btn;
  }

  function probeImage(url) {
    return new Promise(function (resolve) {
      if (!url) {
        resolve(false);
        return;
      }
      var img = new Image();
      img.onload = function () {
        resolve(true);
      };
      img.onerror = function () {
        resolve(false);
      };
      img.src = url;
    });
  }

  function findRow(map, handle) {
    if (!map || !handle) return null;
    var list = map.products || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].handle === handle) return list[i];
    }
    return null;
  }

  function run(gallery) {
    if (!gallery || gallery.getAttribute('data-kl-foil-injected')) return;
    var handle =
      gallery.getAttribute('data-handle') ||
      (gallery.closest('[data-handle]') && gallery.closest('[data-handle]').getAttribute('data-handle')) ||
      '';
    if (!handle) return;

    var mapUrl = gallery.getAttribute('data-foil-map-url');
    if (!mapUrl) return;

    // Mark early to avoid double-fetch races; clear on soft skip
    gallery.setAttribute('data-kl-foil-injected', 'pending');

    fetch(mapUrl, { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (map) {
        if (!map) {
          gallery.removeAttribute('data-kl-foil-injected');
          return null;
        }
        var row = findRow(map, handle);
        if (!row || !row.embossed) {
          gallery.removeAttribute('data-kl-foil-injected');
          return null;
        }
        var assetBase = assetBaseFromMapUrl(mapUrl);
        var embossedUrl = assetBase + row.embossed;
        return probeImage(embossedUrl).then(function (ok) {
          if (!ok) {
            gallery.removeAttribute('data-kl-foil-injected');
            return null;
          }
          return { row: row, assetBase: assetBase };
        });
      })
      .then(function (ctx) {
        if (!ctx) return;
        if (gallery.querySelector('[data-kl-foil-slide]')) {
          gallery.setAttribute('data-kl-foil-injected', '1');
          return;
        }

        ensureCss();

        var carousel =
          gallery.querySelector('scroll-carousel.product-gallery__carousel') ||
          gallery.querySelector('.product-gallery__carousel');
        if (!carousel) {
          gallery.removeAttribute('data-kl-foil-injected');
          return;
        }

        var mediaList = Array.prototype.slice.call(
          carousel.querySelectorAll(':scope > .product-gallery__media')
        );
        if (!mediaList.length) {
          // :scope unsupported fallback
          mediaList = Array.prototype.filter.call(carousel.children, function (el) {
            return el.classList && el.classList.contains('product-gallery__media');
          });
        }
        if (!mediaList.length) {
          gallery.removeAttribute('data-kl-foil-injected');
          return;
        }

        var built = buildSlide(ctx.row, ctx.assetBase);
        afterThird(mediaList, built.wrap);

        // Thumbnails (Prestige product-gallery-navigation)
        var thumbNav =
          gallery.querySelector('product-gallery-navigation.product-gallery__thumbnail-scroller') ||
          gallery.querySelector('.product-gallery__thumbnail-scroller');
        if (thumbNav) {
          var thumbs = Array.prototype.filter.call(thumbNav.children, function (el) {
            return el.matches && el.matches('button.product-gallery__thumbnail');
          });
          if (thumbs.length) {
            afterThird(thumbs, buildThumb(ctx.row, ctx.assetBase));
          }
        }

        // Page dots (carousel-navigation)
        var dotsNav = gallery.querySelector('carousel-navigation.page-dots');
        if (dotsNav) {
          var dots = Array.prototype.filter.call(dotsNav.children, function (el) {
            return el.matches && el.matches('button');
          });
          if (dots.length) {
            afterThird(dots, buildDot());
          }
        }

        gallery.setAttribute('data-kl-foil-injected', '1');
        gallery.setAttribute('data-kl-foil-handle', ctx.row.handle);

        try {
          gallery.dispatchEvent(
            new CustomEvent('kl:passport-foil-injected', {
              bubbles: true,
              detail: { handle: ctx.row.handle },
            })
          );
        } catch (e) {}
      })
      .catch(function () {
        gallery.removeAttribute('data-kl-foil-injected');
      });
  }

  function boot() {
    ensureCss();
    document.querySelectorAll('product-gallery[data-handle][data-foil-map-url]').forEach(run);
    // Fallback: data-handle only (map URL may live on section)
    document.querySelectorAll('product-gallery[data-handle]:not([data-kl-foil-injected])').forEach(function (g) {
      if (!g.getAttribute('data-foil-map-url')) {
        var section = g.closest('[data-foil-map-url]');
        if (section) g.setAttribute('data-foil-map-url', section.getAttribute('data-foil-map-url'));
      }
      if (g.getAttribute('data-foil-map-url')) run(g);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor / section re-render
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);

  // Expose for smoke / debug
  window.KLProductFoilInject = { boot: boot, run: run };
})();
